import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, ChevronDownIcon, PlusIcon, TrashIcon, PencilIcon, StarIcon } from '@heroicons/react/20/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { showTemporaryMessage } from "../components/Message";
import { AuthContext } from '../components/AuthProvider';

import {
	get_diseases,
	post_diseases,
} from "../tools-network";

const EditDiseases = () => {
	const { t } = useTranslation();
	const { loginToken } = React.useContext(AuthContext);

	const [diseases, setDiseases] = useState([]);
	const [expandedNodes, setExpandedNodes] = useState(new Set());
	const [editingNode, setEditingNode] = useState(null);
	const [editingValue, setEditingValue] = useState('');
	const [hasChanges, setHasChanges] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		loadDiseases();
	}, []);

	const loadDiseases = async () => {
		try {
			const res = await get_diseases(loginToken);
			if (res.status) {
				setDiseases(buildHierarchy(res.data));
				setHasChanges(false);
			} else {
				showTemporaryMessage("Fetch diseases FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Fetch diseases FAILED!", err, 'danger');
		}
	};

	const buildHierarchy = (flatData) => {
		const map = {};
		flatData.forEach((item) => {
			map[item.id] = {
				id: item.id,
				name: item.name,
				parent_id: item.parent_id,
				version: item.version,
				prioritized: item.prioritized || false,
				children: []
			};
		});

		const tree = [];
		flatData.forEach((item) => {
			if (item.parent_id === null) {
				tree.push(map[item.id]);
			} else if (map[item.parent_id]) {
				map[item.parent_id].children.push(map[item.id]);
			}
		});
		return tree;
	};

	const flattenHierarchy = (nodes, parentId = null) => {
		const result = [];
		nodes.forEach(node => {
			result.push({
				id: node.id,
				name: node.name,
				parent_id: parentId,
				version: node.version,
				prioritized: node.prioritized || false
			});
			if (node.children && node.children.length > 0) {
				result.push(...flattenHierarchy(node.children, node.id));
			}
		});
		return result;
	};

	const toggleExpand = (nodeId) => {
		const newExpanded = new Set(expandedNodes);
		if (newExpanded.has(nodeId)) {
			newExpanded.delete(nodeId);
		} else {
			newExpanded.add(nodeId);
		}
		setExpandedNodes(newExpanded);
	};

	const startEditing = (node) => {
		setEditingNode(node.id);
		setEditingValue(node.name);
	};

	const saveEdit = () => {
		if (editingNode && editingValue.trim()) {
			updateNodeName(diseases, editingNode, editingValue.trim());
			setDiseases([...diseases]);
			setHasChanges(true);
		}
		setEditingNode(null);
		setEditingValue('');
	};

	const cancelEdit = () => {
		setEditingNode(null);
		setEditingValue('');
	};

	const updateNodeName = (nodes, nodeId, newName) => {
		for (let node of nodes) {
			if (node.id === nodeId) {
				node.name = newName;
				return true;
			}
			if (node.children && updateNodeName(node.children, nodeId, newName)) {
				return true;
			}
		}
		return false;
	};

	const addChild = (parentNode) => {
		const newId = `temp_${Date.now()}`;
		const newNode = {
			id: newId,
			name: 'New Item',
			parent_id: parentNode ? parentNode.id : null,
			prioritized: false,
			children: []
		};

		if (parentNode) {
			addNodeAsChild(diseases, parentNode.id, newNode);
			setExpandedNodes(new Set([...expandedNodes, parentNode.id]));
		} else {
			diseases.push(newNode);
		}
		
		setDiseases([...diseases]);
		setHasChanges(true);
		setEditingNode(newId);
		setEditingValue('New Item');
	};

	const addNodeAsChild = (nodes, parentId, newNode) => {
		for (let node of nodes) {
			if (node.id === parentId) {
				if (!node.children) node.children = [];
				node.children.push(newNode);
				return true;
			}
			if (node.children && addNodeAsChild(node.children, parentId, newNode)) {
				return true;
			}
		}
		return false;
	};

	const deleteNode = (nodeToDelete) => {
		if (!window.confirm(`Delete "${nodeToDelete.name}"${nodeToDelete.children?.length > 0 ? ' and all its children' : ''}?`)) {
			return;
		}
		removeNode(diseases, nodeToDelete.id);
		setDiseases([...diseases]);
		setHasChanges(true);
	};

	const removeNode = (nodes, nodeId) => {
		for (let i = 0; i < nodes.length; i++) {
			if (nodes[i].id === nodeId) {
				nodes.splice(i, 1);
				return true;
			}
			if (nodes[i].children && removeNode(nodes[i].children, nodeId)) {
				return true;
			}
		}
		return false;
	};

	const togglePriority = (node) => {
		toggleNodePriority(diseases, node.id);
		setDiseases([...diseases]);
		setHasChanges(true);
	};

	const toggleNodePriority = (nodes, nodeId) => {
		for (let node of nodes) {
			if (node.id === nodeId) {
				node.prioritized = !node.prioritized;
				return true;
			}
			if (node.children && toggleNodePriority(node.children, nodeId)) {
				return true;
			}
		}
		return false;
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const flatData = flattenHierarchy(diseases);
			const response = await post_diseases(loginToken, flatData);
			if (response.status) {
				showTemporaryMessage("Diseases updated successfully", "", 'success');
				await loadDiseases();
			} else {
				showTemporaryMessage("Failed to update diseases", response.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Error updating diseases", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	};

	const renderNode = (node, level = 0) => {
		const isExpanded = expandedNodes.has(node.id);
		const hasChildren = node.children && node.children.length > 0;
		const isEditing = editingNode === node.id;

		return (
			<div key={node.id} className="mb-1">
				<div 
					className="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded group"
					style={{ marginLeft: `${level * 24}px` }}
				>
					{/* Expand/collapse button */}
					<button
						onClick={() => toggleExpand(node.id)}
						className="w-5 h-5 flex items-center justify-center"
					>
						{hasChildren ? (
							isExpanded ? (
								<ChevronDownIcon className="w-4 h-4 text-gray-500" />
							) : (
								<ChevronRightIcon className="w-4 h-4 text-gray-500" />
							)
						) : (
							<span className="w-4" />
						)}
					</button>

					{/* Name or edit input */}
					{isEditing ? (
						<div className="flex-1 flex items-center gap-2">
							<input
								type="text"
								value={editingValue}
								onChange={(e) => setEditingValue(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') saveEdit();
									if (e.key === 'Escape') cancelEdit();
								}}
								className="flex-1 px-2 py-1 border border-cyan-600 rounded focus:outline-none focus:ring-2 focus:ring-cyan-600"
								autoFocus
							/>
							<button
								onClick={saveEdit}
								className="btn-main text-white px-3 py-1 rounded text-sm"
							>
								Save
							</button>
							<button
								onClick={cancelEdit}
								className="border border-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-100"
							>
								Cancel
							</button>
						</div>
					) : (
						<>
							<span className="flex-1 text-gray-900">{node.name}</span>
							
							{/* Priority star */}
							<button
								onClick={() => togglePriority(node)}
								className="p-1 hover:bg-yellow-50 rounded"
								title={node.prioritized ? "Remove from priority list" : "Add to priority list"}
							>
								{node.prioritized ? (
									<StarIcon className="w-5 h-5 text-yellow-500" />
								) : (
									<StarIconOutline className="w-5 h-5 text-gray-300" />
								)}
							</button>
							
							{/* Action buttons - show on hover */}
							<div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
								<button
									onClick={() => addChild(node)}
									className="p-1 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded"
									title="Add child"
								>
									<PlusIcon className="w-4 h-4" />
								</button>
								<button
									onClick={() => startEditing(node)}
									className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded"
									title="Rename"
								>
									<PencilIcon className="w-4 h-4" />
								</button>
								<button
									onClick={() => deleteNode(node)}
									className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
									title="Delete"
								>
									<TrashIcon className="w-4 h-4" />
								</button>
							</div>
						</>
					)}
				</div>

				{/* Render children */}
				{hasChildren && isExpanded && (
					<div>
						{node.children.map(child => renderNode(child, level + 1))}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="flex-grow w-full">
			{/* Header */}
			<div className="w-full bg-light-blue shadow mb-10">
				<div className="max-w-screen-xl mx-auto py-4 px-4">
					<h1 className="text-4xl font-extrabold text-black">Edit Diseases</h1>
				</div>
			</div>

			<div className="max-w-screen-xl mx-auto px-4">
				{/* Instructions */}
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
					<p className="text-sm text-gray-700">
						Manage your disease hierarchy. Click the <StarIcon className="w-4 h-4 inline text-yellow-500" /> star to prioritize (shown first in lists), 
						<PlusIcon className="w-4 h-4 inline mx-1" /> to add children, 
						<PencilIcon className="w-4 h-4 inline mx-1" /> to rename, or <TrashIcon className="w-4 h-4 inline" /> to delete items.
						Don't forget to save your changes!
					</p>
				</div>

				{/* Action bar */}
				<div className="flex justify-between items-center mb-4">
					<button
						onClick={() => addChild(null)}
					disabled={isSaving}
					className="btn-main text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<PlusIcon className="w-5 h-5" />
					Add Root Category
				</button>

					<div className="flex gap-3">
						<button
							onClick={loadDiseases}
							disabled={isSaving}
							className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Reset
						</button>
						<button
							onClick={handleSave}
							disabled={!hasChanges || isSaving}
							className="btn-secondary text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isSaving ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>

				{/* Disease tree */}
			<div className={`bg-white rounded-lg p-4 min-h-[400px] border-2 transition-colors relative ${
				hasChanges ? 'border-red-500 shadow-lg shadow-red-100' : 'border-gray-200'
			} ${
				isSaving ? 'pointer-events-none opacity-60' : ''
			}`}>
				{diseases.length === 0 ? (
					<div className="text-center text-gray-500 py-12">
						No diseases yet. Click "Add Root Category" to get started.
					</div>
				) : (
					<div>
						{diseases.map(node => renderNode(node))}
					</div>
				)}
			</div>

			{/* Change indicator */}
			{hasChanges && (
				<div className="mt-4 text-sm text-orange-600 flex items-center gap-2">
					<span className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></span>
					You have unsaved changes
				</div>
			)}
			</div>
		</div>
	);
};

export default EditDiseases;

